// Phase 2 P2-1 — THE DESK page.
//
// Daily intelligence publication surface. Renders today's specialist
// selections from already-published products, Clash Flags, and the 5 Board
// Formulas (Standing Ten / Chaos Day / Unseen / The Anchor / The
// Contrarian). Cron at 11:00 GMT publishes desk_picks; cron at 11:30
// GMT builds desk_formulas. Both are read-only here.
//
// Truth contract:
//   - Abstentions are surfaced (not hidden) — per spec § "Abstain Protocol".
//   - Conviction tier badges are validated by DB trigger; we only display.
//   - Formulas that did not publish today render their skip_reason — a
//     skipped formula is the system working correctly, not failing.

import type { CSSProperties } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { AwaitingDataPanel } from "../_awaiting-data.tsx";
import { marketLabel, selectionLabel, formatLeg } from "../../exchange/market-format.ts";
import "../../styles/desk-premium.css";
import {
  useDeskToday,
  type DeskFormulaTodayRow,
  type DeskPickRow,
  type DeskClashFlagRow,
} from "../../desk/useDeskToday.ts";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";

// ─── Static formula display config ─────────────────────────────────

const FORMULA_DISPLAY: Record<
  DeskFormulaTodayRow["formula_name"],
  { title: string; tag: string; tagline: string }
> = {
  standing_ten: {
    title: "Standing Ten",
    tag: "Top 10 published",
    tagline: "The daily ten. Discipline compounded.",
  },
  chaos_day: {
    title: "Chaos Day",
    tag: "10–12 mispriced",
    tagline: "Where the market is most wrong.",
  },
  unseen: {
    title: "Unseen",
    tag: "85%+ confidence",
    tagline: "The market hasn't caught up yet.",
  },
  the_anchor: {
    title: "The Anchor",
    tag: "Single max-conviction",
    tagline: "One pick. Every signal pointing the same way.",
  },
  the_contrarian: {
    title: "The Contrarian",
    tag: "Long-price value",
    tagline: "Longer prices with recorded positive value.",
  },
};

const FORMULA_ORDER: DeskFormulaTodayRow["formula_name"][] = [
  "the_anchor",
  "standing_ten",
  "unseen",
  "chaos_day",
  "the_contrarian",
];

// ─── Styles ────────────────────────────────────────────────────────

const pageRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

const pageHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  flexWrap: "wrap",
  gap: "var(--sz-space-2)",
};

const pageTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
  margin: 0,
};

const pageSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: "4px",
};

const sectionEyebrow: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  margin: "var(--sz-space-3) 0 var(--sz-space-2)",
};

const card: CSSProperties = {
  background: "var(--sz-bg-surface)",
  border: "1px solid var(--sz-border-default)",
  borderRadius: "var(--sz-radius-md, 8px)",
  padding: "var(--sz-space-3)",
};

const formulaGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "var(--sz-space-3)",
};

const formulaCard: CSSProperties = {
  ...card,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
};

const formulaCardSkipped: CSSProperties = {
  ...formulaCard,
  opacity: 0.55,
};

const formulaTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.4rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  margin: 0,
};

const formulaTagline: CSSProperties = {
  fontFamily: "var(--sz-font-body, 'DM Sans')",
  fontSize: "0.85rem",
  color: "var(--sz-text-secondary)",
  fontStyle: "italic",
  margin: 0,
};

const formulaMeta: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-muted)",
  letterSpacing: "1px",
  textTransform: "uppercase",
};

const oddsValue: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.8rem",
  color: "var(--sz-amber)",
  letterSpacing: "1px",
};

const legRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 0",
  borderBottom: "1px solid var(--sz-border-subtle)",
  fontSize: "0.85rem",
  gap: 12,
};

const pickGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "var(--sz-space-2)",
};

const pickCard: CSSProperties = {
  ...card,
  padding: "var(--sz-space-2)",
};

const pickCardAbstain: CSSProperties = {
  ...pickCard,
  opacity: 0.6,
};

const agentLine: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
  textTransform: "uppercase",
};

const convictionBadge = (tier: string | null): CSSProperties => ({
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: 3,
  border: "1px solid",
  ...(tier === "maximum"
    ? { color: "var(--sz-amber)", borderColor: "var(--sz-amber-dim)" }
    : tier === "high"
      ? { color: "var(--sz-success-text)", borderColor: "var(--sz-success-dim)" }
      : tier === "standard"
        ? { color: "var(--sz-text-secondary)", borderColor: "var(--sz-border-default)" }
        : { color: "var(--sz-text-muted)", borderColor: "var(--sz-border-subtle)" }),
});

const clashCard: CSSProperties = {
  ...card,
  borderLeft: "3px solid var(--sz-amber)",
};

// ─── Helpers ───────────────────────────────────────────────────────

const fmtOdds = (n: number | null): string =>
  n === null || !Number.isFinite(n) ? "—" : `${n.toFixed(2)}x`;
const fmtPct = (n: number | null): string =>
  n === null || !Number.isFinite(n) ? "—" : `${n.toFixed(1)}%`;
const fmtConf = (n: number | null): string =>
  n === null || !Number.isFinite(n) ? "—" : Math.round(n).toString();

// ─── Pick card ─────────────────────────────────────────────────────

// Human match line: "Sunderland vs Chelsea" with the picked side
// highlighted. Falls back to selection-only when fixture context isn't
// resolved (older picks / abstentions).
function matchHero(pick: DeskPickRow): JSX.Element {
  const home = pick.home_team;
  const away = pick.away_team;
  const sel = (pick.selection ?? "").toLowerCase();
  if (home && away) {
    const homePicked = sel === "home" || sel === home.toLowerCase();
    const awayPicked = sel === "away" || sel === away.toLowerCase();
    return (
      <span>
        <span className={homePicked ? "pickside" : undefined}>{home}</span>
        <span style={{ opacity: 0.5 }}> vs </span>
        <span className={awayPicked ? "pickside" : undefined}>{away}</span>
      </span>
    );
  }
  // No fixture context — lead with the selection itself.
  return <span className="pickside">{pick.selection ?? "—"}</span>;
}

function fmtKick(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" });
}

function PickCard({ pick }: { pick: DeskPickRow }): JSX.Element {
  const tier = pick.conviction_tier ?? "";
  const conf = pick.confidence_score;
  const confPct = conf !== null && Number.isFinite(conf) ? Math.max(0, Math.min(100, conf)) : 0;
  const edgePp = pick.clv_projection_pp;
  const dna = (pick.primary_dna_factors ?? []).filter(Boolean).slice(0, 4);
  const ctxBits = [pick.competition, fmtKick(pick.kickoff_at)].filter(Boolean);
  const sourceLabel = [pick.source_product, pick.source_tier]
    .filter(Boolean)
    .join(" · ")
    .replace(/_/g, " ");

  return (
    <article className={`desk-prem-card tier-${tier}`}>
      <div
        className="desk-prem-context"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <span>Agent {String(pick.agent_id).padStart(2, "0")}</span>
        <span>{pick.agent_domain}</span>
      </div>

      <div className="desk-prem-match">{matchHero(pick)}</div>
      {ctxBits.length > 0 && <div className="desk-prem-context">{ctxBits.join(" · ")}</div>}

      <div className="desk-prem-pickline">
        {marketLabel(pick.market)} · <b>{selectionLabel(pick.market, pick.selection)}</b>
      </div>

      <div className="desk-prem-metrics">
        <span className="desk-prem-odds">{fmtOdds(pick.odds_at_submission)}</span>
        <span className="desk-prem-metric">
          <span className="lbl">EV</span>
          <span className="val">{fmtPct(pick.ev_estimate)}</span>
        </span>
        <span className="desk-prem-metric">
          <span className="lbl">Conf {fmtConf(conf)}</span>
          <span className="desk-prem-confbar">
            <span style={{ width: `${confPct}%` }} />
          </span>
        </span>
        {tier && (
          <span className={`desk-prem-pill tier-${tier}`}>
            {pick.source_product ? "selected" : tier}
          </span>
        )}
        {edgePp !== null && Number.isFinite(edgePp) && edgePp >= 1 && (
          <span className="desk-prem-edge">+{Math.round(edgePp)}pp edge</span>
        )}
      </div>

      {sourceLabel && (
        <div className="desk-prem-context">
          Source: {sourceLabel}
          {pick.source_agreement_count > 1
            ? ` · ${pick.source_agreement_count} product confirmations`
            : ""}
        </div>
      )}

      {dna.length > 0 && (
        <div className="desk-prem-dna">
          {dna.map((f) => (
            <span key={f} className="chip">
              {f}
            </span>
          ))}
        </div>
      )}

      {pick.reasoning && <div className="desk-prem-reason">{pick.reasoning}</div>}
    </article>
  );
}

// Abstention — surfaced with dignity (honest, not failed).
function AbstainCard({ pick }: { pick: DeskPickRow }): JSX.Element {
  return (
    <div className="desk-prem-abstain">
      <div className="agent">
        <span>Agent {String(pick.agent_id).padStart(2, "0")}</span>
        <span>{pick.agent_domain}</span>
      </div>
      <div className="reason">{pick.abstain_reason ?? "No qualifying edge in domain today."}</div>
    </div>
  );
}

// ─── Formula card ──────────────────────────────────────────────────

function FormulaCard({ formula }: { formula: DeskFormulaTodayRow }): JSX.Element {
  const display = FORMULA_DISPLAY[formula.formula_name];
  const isPublished = formula.publication_status === "published";
  return (
    <article style={isPublished ? formulaCard : formulaCardSkipped}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h3 style={formulaTitle}>{display.title}</h3>
        <span style={formulaMeta}>{display.tag}</span>
      </header>
      <p style={formulaTagline}>{display.tagline}</p>
      {isPublished ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={oddsValue}>{fmtOdds(formula.combined_odds)}</span>
            <span style={formulaMeta}>
              {formula.leg_count} leg{formula.leg_count === 1 ? "" : "s"}
            </span>
          </div>
          <div style={{ marginTop: "var(--sz-space-2)" }}>
            {formula.legs.map((leg) => (
              <div key={leg.pick_id} style={legRow}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                  <span
                    style={{
                      color: "var(--sz-text-primary)",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    {leg.home_team && leg.away_team
                      ? `${leg.home_team} vs ${leg.away_team}`
                      : leg.fixture_id}
                  </span>
                  <span style={{ color: "var(--sz-text-secondary)", fontSize: "0.8rem" }}>
                    {formatLeg(leg.market, leg.selection)}
                    {leg.competition ? ` · ${leg.competition}` : ""}
                  </span>
                </div>
                <span
                  style={{
                    fontFamily: "var(--sz-font-mono)",
                    color: "var(--sz-amber)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fmtOdds(leg.odds)}
                </span>
              </div>
            ))}
          </div>
          {formula.cross_formula_overlap && formula.cross_formula_overlap.length > 0 ? (
            <div style={{ fontSize: "0.75rem", color: "var(--sz-amber)", marginTop: 8 }}>
              <Icon name="info" size={12} /> Cross-formula convergence:{" "}
              {formula.cross_formula_overlap.join(", ")}
            </div>
          ) : null}
        </>
      ) : (
        <div
          style={{
            fontFamily: "var(--sz-font-mono)",
            fontSize: "0.8rem",
            color: "var(--sz-text-muted)",
            marginTop: 8,
          }}
        >
          {formula.skip_reason ? formula.skip_reason.replace(/_/g, " ") : "did not publish today"}
        </div>
      )}
    </article>
  );
}

// ─── Clash flag card ──────────────────────────────────────────────

function ClashFlagCard({ flag }: { flag: DeskClashFlagRow }): JSX.Element {
  return (
    <article style={clashCard}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span
          style={{
            fontFamily: "var(--sz-font-mono)",
            letterSpacing: "1px",
            color: "var(--sz-amber)",
            textTransform: "uppercase",
            fontSize: "var(--sz-mono-xs)",
          }}
        >
          [CLASH] · {flag.agent_count} agents converging
        </span>
        {flag.identical_pick ? (
          <span
            style={{
              fontFamily: "var(--sz-font-mono)",
              fontSize: "9px",
              color: "var(--sz-success-text)",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Identical pick
          </span>
        ) : null}
      </div>
      <div style={{ fontSize: "0.95rem", color: "var(--sz-text-primary)", marginTop: 6 }}>
        {flag.fixture_id}
      </div>
      <div
        style={{
          fontFamily: "var(--sz-font-mono)",
          fontSize: "var(--sz-mono-xs)",
          color: "var(--sz-text-secondary)",
          marginTop: 4,
        }}
      >
        Domains: {flag.agent_domains.join(" · ")}
      </div>
      <div
        style={{
          fontFamily: "var(--sz-font-mono)",
          fontSize: "var(--sz-mono-xs)",
          color: "var(--sz-text-muted)",
          marginTop: 2,
        }}
      >
        Selections: {flag.selections.join(", ")}
      </div>
    </article>
  );
}

// ─── Page ──────────────────────────────────────────────────────────

export function DeskPage(): JSX.Element {
  const { picks, clashFlags, formulas, loading, error } = useDeskToday();

  const hasDeskPicks = picks.length > 0;
  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: hasDeskPicks });

  const submitted = picks.filter((p) => p.status === "submitted");
  const reads = picks.filter((p) => p.status === "read");
  const abstentions = picks.filter((p) => p.status === "abstain");
  const formulaMap = new Map(formulas.map((f) => [f.formula_name, f]));

  return (
    <div style={pageRoot}>
      <div style={pageHeader}>
        <div>
          <h1 style={pageTitle}>The Desk</h1>
          <div style={pageSubtitle}>
            Zero-cost product selector · 5 daily formulas · published 11:00 GMT
          </div>
        </div>
        <div
          style={{
            fontFamily: "var(--sz-font-mono)",
            fontSize: "var(--sz-mono-xs)",
            letterSpacing: "1px",
            color: "var(--sz-text-muted)",
          }}
        >
          {submitted.length} submitted · {reads.length} reads · {abstentions.length} abstained ·{" "}
          {clashFlags.length} clash{clashFlags.length === 1 ? "" : "es"}
        </div>
      </div>

      {loading ? (
        <AwaitingDataPanel
          eyebrow="The Desk"
          title="Loading…"
          body="Fetching today's agent submissions and formula publications."
        />
      ) : error !== null ? (
        <>
          <AwaitingDataPanel eyebrow="The Desk" title="Read failed." body={error} />
          <DoctrineSection
            probabilityPicks={doctrineProbPicks}
            valuePicks={doctrineValuePicks}
            isFullFallback
            substrateEmpty={substrateEmpty}
            substrateWeek={substrateWeek}
          />
        </>
      ) : submitted.length === 0 && reads.length === 0 && abstentions.length === 0 ? (
        <>
          <AwaitingDataPanel
            eyebrow="The Desk"
            title="No publication for today yet."
            body="The 11:00 GMT selector has not published yet. It will choose from STRIKZA's already-published product tiers without another paid analysis run."
          />
          <DoctrineSection
            probabilityPicks={doctrineProbPicks}
            valuePicks={doctrineValuePicks}
            isFullFallback
            substrateEmpty={substrateEmpty}
            substrateWeek={substrateWeek}
          />
        </>
      ) : (
        <>
          {/* Formulas first — the headline compound products */}
          <div style={sectionEyebrow}>Daily Formulas</div>
          <div style={formulaGrid}>
            {FORMULA_ORDER.map((name) => {
              const formula = formulaMap.get(name);
              if (!formula) {
                return (
                  <article key={name} style={formulaCardSkipped}>
                    <h3 style={formulaTitle}>{FORMULA_DISPLAY[name].title}</h3>
                    <p style={formulaTagline}>{FORMULA_DISPLAY[name].tagline}</p>
                    <div
                      style={{
                        fontFamily: "var(--sz-font-mono)",
                        fontSize: "0.8rem",
                        color: "var(--sz-text-muted)",
                        marginTop: 8,
                      }}
                    >
                      not yet built today
                    </div>
                  </article>
                );
              }
              return <FormulaCard key={name} formula={formula} />;
            })}
          </div>

          {/* Clash flags */}
          {clashFlags.length > 0 ? (
            <>
              <div style={sectionEyebrow}>Clash Flags · independent specialists converged</div>
              <div style={pickGrid}>
                {clashFlags.map((flag) => (
                  <ClashFlagCard key={`${flag.fixture_id}`} flag={flag} />
                ))}
              </div>
            </>
          ) : null}

          {/* All submitted picks */}
          {submitted.length > 0 ? (
            <>
              <div style={sectionEyebrow}>Today's Picks ({submitted.length})</div>
              <div className="desk-prem-grid">
                {submitted.map((p) => (
                  <PickCard key={p.id} pick={p} />
                ))}
              </div>
            </>
          ) : null}

          {/* Model reads — every agent with fixtures shows its best read
              (LEAN = sub-BET edge to monitor, READ = model line), so no game
              goes to waste even when no BET-grade edge clears. */}
          {reads.length > 0 ? (
            <>
              <div style={sectionEyebrow}>Model Reads ({reads.length}) · monitor, not bets</div>
              <div className="desk-prem-grid">
                {reads.map((p) => (
                  <PickCard key={p.id} pick={p} />
                ))}
              </div>
            </>
          ) : null}

          {/* Abstentions surfaced last — dignified, quiet strip */}
          {abstentions.length > 0 ? (
            <>
              <div style={sectionEyebrow}>
                Abstentions ({abstentions.length}) · honest, not failed
              </div>
              <div className="desk-prem-grid abstain">
                {abstentions.map((p) => (
                  <AbstainCard key={p.id} pick={p} />
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
